import { spErrorTags } from "@eventr/common";
import { ColoursObj, Typography } from "@eventr/common-storybook";
import { useNativeNavigationViewContext } from "@eventr/native/navigation/react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { PopoverItemProps, ToolbarItemProps } from "../layout/TopToolbar";
import { isModalRoot, isTrailingRoot } from "./modalRoot";
import { useSplitStore } from "./split.store";
import { ensureToolbarIcon, getToolbarIcon, TOOLBAR_ICON_SCALE } from "./toolbarIcons";

type RightItem = { id: string; title: string; image?: { uri: string; scale?: number }; enabled: boolean };

/** Not user-visible; only has to be unique among one screen's item ids. */
const DERIVED_CLOSE_BUTTON_ID = "nativeToolbarDerivedClose";

/** Not user-visible; only has to be unique among one screen's item ids. */
const DERIVED_TRAILING_BACK_BUTTON_ID = "nativeToolbarDerivedTrailingBack";

// Not a bar button — shares the bar buttons' click-event id namespace, so it must not
// collide with a real item's id.
const DERIVED_BACK_BUTTON_ID = "nativeToolbarDerivedBack";

// Sentinel for a refused swipe-down-dismiss; matches NativeNavigation.swift's `dismissAttemptButtonId`.
const DERIVED_DISMISS_BUTTON_ID = "nativeToolbarDerivedDismiss";

/**
 * Renders the NavigationView leading/trailing items as native navigation bar buttons
 * on iOS. Icons are rasterised to PNG (UIImage cannot decode SVG) and cached; a native
 * tap is bridged from the plugin's click event to the item's action, and trailing
 * items with a menu toggle the same popover the web header uses. iOS only (rendered
 * only under native navigation). `leading` is for views with no native back chrome of
 * their own (e.g. a pageSheet root) that still need a close/back button.
 *
 * A sheet's root view is flagged in its `ViewSpec.state` when presented (see
 * `modalEntry` in init.ts). Given that flag and no `leading` of its own, this
 * synthesises a close (X) button calling `onClose`, so a route is dismissable just by
 * being a sheet. An explicit `leading` always wins. `onClose` rather than a native
 * dismiss because screens guard their exit, and dismissing behind their back would
 * leave the router thinking the sheet was open.
 *
 * The first screen of an expanded split's trailing column is flagged the same way (see
 * `isTrailingRoot`) and gets a derived back chevron calling `onClose`.
 *
 * A pushed view declares this as its `backButtonId`, so a back attempt routes through
 * the same click event as bar buttons and dispatches to `onClose`, which decides whether to navigate.
 *
 * `transparentBar` keeps the bar visible (so leftItems/rightItems still draw as real
 * native buttons) but makes it see-through and shadowless, for a full-bleed image
 * header where the image should show through behind the bar instead of a solid strip.
 */
export function NativeToolbar({
  leading,
  trailing,
  transparentBar,
  onClose,
  cancellable,
  onDismissAttempt,
  supplementBackButton,
  backMayRefuse,
}: {
  leading?: ToolbarItemProps[];
  trailing?: ToolbarItemProps[];
  transparentBar?: boolean;
  onClose?: () => void;
  cancellable?: boolean;
  // Called when swipe-down dismiss was refused (cancellable false). Mirrors onClose but
  // should end in dismiss(), not a pop. iOS only.
  onDismissAttempt?: () => void;
  // Keeps the system back chevron and its swipe-to-pop gesture alongside the custom leading
  // items on a pushed view, instead of the items replacing them.
  supplementBackButton?: boolean;
  // Whether a back attempt (tap or swipe) might actually be refused. Left undefined for
  // almost every screen, which now defaults to false (never refuses) - native pops
  // directly, tracking an iOS swipe's finger, and onClose is never asked. Pass true only
  // where onClose may do something other than navigate away (e.g. a discard-unsaved-
  // changes dialog), so a swipe is routed through onClose to give it the chance to refuse.
  backMayRefuse?: boolean;
}) {
  const [cacheVersion, setCacheVersion] = useState(0);
  const [menu, setMenu] = useState<PopoverItemProps[] | null>(null);

  // Read state before the options-carrying call below, since the derived button
  // depends on it. Passing no options makes this a pure read: the hook only pushes a
  // native update when options are given, so calling it twice per render is safe.
  const viewState = useNativeNavigationViewContext().state;
  const isSheetRoot = isModalRoot(viewState);
  // Collapsed, the column's first screen is nested over the list and has the system chevron.
  const splitCollapsed = useSplitStore((s) => s.collapsed);
  const isTrailingColumnRoot = isTrailingRoot(viewState) && !splitCollapsed;

  const hasExplicitLeading = !!leading && leading.length > 0;

  const derivedLeading = useMemo<ToolbarItemProps[] | undefined>(() => {
    if (hasExplicitLeading || !onClose) return undefined;
    if (isSheetRoot) return [{ id: DERIVED_CLOSE_BUTTON_ID, icon: "Cross", action: onClose }];
    if (isTrailingColumnRoot) return [{ id: DERIVED_TRAILING_BACK_BUTTON_ID, icon: "Chevron_Left", action: onClose }];
    return undefined;
  }, [isSheetRoot, isTrailingColumnRoot, hasExplicitLeading, onClose]);

  const effectiveLeading = hasExplicitLeading ? leading : derivedLeading;

  // Only a pushed view: a sheet root already has the derived Close button for onClose;
  // declaring a back id there would double-claim the same exit.
  const handlesBackAttempts = !!onClose && !isSheetRoot;

  // Rasterise any not-yet-cached icons, then bump the version to pick them up. Mostly
  // a no-op since toolbar icons are pre-warmed at startup.
  useEffect(() => {
    let cancelled = false;
    const pending = [...(effectiveLeading ?? []), ...(trailing ?? [])].filter(
      (item) => item.icon && !getToolbarIcon(item.icon),
    );
    if (pending.length === 0) return;
    Promise.all(pending.map((item) => ensureToolbarIcon(item.icon!))).then(() => {
      if (!cancelled) setCacheVersion((v) => v + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [effectiveLeading, trailing]);

  // Build the bar buttons synchronously from the cache (cacheVersion forces a rebuild
  // once icons finish rasterising).
  const toRightItem = (item: ToolbarItemProps): RightItem => {
    const title = item.buttonText ?? "";
    const cached = item.icon ? getToolbarIcon(item.icon) : undefined;
    // `enabled` maps to the native button's enabled state; always sent (not omitted) so
    // re-enabling isn't left stale from a reused button's previous spec.
    const enabled = !item.disabled;
    return cached
      ? { id: item.id, title, image: { uri: cached, scale: TOOLBAR_ICON_SCALE }, enabled }
      : { id: item.id, title, enabled };
  };

  const leftItems = useMemo<RightItem[]>(() => {
    void cacheVersion;
    return (effectiveLeading ?? []).map(toRightItem);
  }, [effectiveLeading, cacheVersion]);

  const rightItems = useMemo<RightItem[]>(() => {
    void cacheVersion;
    return (trailing ?? []).map(toRightItem);
  }, [trailing, cacheVersion]);

  // Only send leftItems when there are leading items: the plugin treats *any* leftItems
  // value (including an empty array) as "custom left bar buttons", which suppresses the
  // automatic native back button. Omitting the key entirely keeps the default back chrome.
  // leftItemsSupplementBackButton, sent only when the caller asks for it, keeps the system
  // chevron and its swipe-to-pop gesture showing alongside the custom items.
  const ctx = useNativeNavigationViewContext({
    stackItem: {
      ...(leftItems.length
        ? {
            leftItems,
            ...(supplementBackButton && handlesBackAttempts ? { leftItemsSupplementBackButton: true } : {}),
          }
        : // Clears a trailing column root's derived chevron once the split collapses: an update
          // keeps whatever it omits.
          isTrailingRoot(viewState)
          ? { leftItems: null }
          : {}),
      rightItems,
      // #00000000 (fully transparent) tells the plugin to strip the bar's background
      // material so the content behind it shows through; translucent must be false/
      // omitted (translucent forces a 50% blur instead of a clear bar). "transparent"
      // is not a valid colour: the plugin only parses hex, and an unparseable colour
      // throws and drops the whole stackItem update (buttons included).
      ...(transparentBar ? { bar: { background: { color: "#00000000" }, iOS: { hideShadow: true } } } : {}),
    },
    // Explicit null (not omitted) clears backButtonId on native; an update only carries
    // what it names, so omitting it would leave a stale id from a reused view.
    backButtonId: handlesBackAttempts ? DERIVED_BACK_BUTTON_ID : null,
    // Same reasoning as backButtonId: explicit null clears a stale value rather than
    // leaving a reused view's prior value. Defaults to false (never refuses) rather than
    // native's own default (may refuse), so swipe-back works unless a screen opts in.
    backButtonMayRefuse: handlesBackAttempts ? (backMayRefuse ?? false) : null,
    // Same reasoning as backButtonId: explicit null clears a stale value rather than leaving it.
    cancellable: cancellable ?? null,
  });

  // Bridge native taps to the item's action / menu. Subscribe once; read the current
  // items and context through refs so the listener is not re-created each render.
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const leadingRef = useRef(effectiveLeading);
  leadingRef.current = effectiveLeading;
  const trailingRef = useRef(trailing);
  trailingRef.current = trailing;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onDismissAttemptRef = useRef(onDismissAttempt);
  onDismissAttemptRef.current = onDismissAttempt;
  useEffect(() => {
    return ctxRef.current.addClickListener(({ buttonId }) => {
      // Matched before the generic lookup below, so a back attempt isn't reported as a
      // swallowed tap by mistake.
      if (buttonId === DERIVED_BACK_BUTTON_ID) {
        onCloseRef.current?.();
        return;
      }
      if (buttonId === DERIVED_DISMISS_BUTTON_ID) {
        onDismissAttemptRef.current?.();
        return;
      }
      const item = leadingRef.current?.find((i) => i.id === buttonId) ?? trailingRef.current?.find((i) => i.id === buttonId);
      // Neither branch below should be reachable for a correctly-specified button; log
      // rather than fail silently on a swallowed tap.
      if (!item) {
        spErrorTags("Native toolbar tap matched no item", ["NATIVE_TOOLBAR"], {
          buttonId,
          known: [...(leadingRef.current ?? []), ...(trailingRef.current ?? [])].map((i) => i.id),
        });
        return;
      }
      // Diagnostic only: disabled buttons don't dispatch natively, so this should be dead
      // code. If it fires, the disabled flag failed to reach native.
      if (item.disabled) {
        spErrorTags("Native toolbar tap ignored, item is disabled", ["NATIVE_TOOLBAR"], { buttonId });
        return;
      }
      item.action?.();
      if (item.popoverItems && item.popoverItems.length > 0) {
        const items = item.popoverItems;
        // Toggle: a second tap on the same button closes the menu.
        setMenu((current) => (current ? null : items));
      }
    });
  }, []);

  if (!menu) return null;

  // Rendered inline (not via a portal) so it appears in this native view's document
  // rather than the main launcher webview, which a MUI Popover would portal to.
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100000 }} onClick={() => setMenu(null)}>
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          position: "fixed",
          top: "calc(var(--native-navigation-inset-top, env(safe-area-inset-top, 0px)) + 4px)",
          right: "8px",
          minWidth: "200px",
          background: ColoursObj.backgrounds.primary,
          border: "1px solid " + ColoursObj.shades.shade3,
          borderRadius: "8px",
          boxShadow: "0px 8px 16px 0px rgba(16, 55, 92, 0.1)",
          overflow: "hidden",
        }}
      >
        {menu.map((popoverItem, index) => {
          const disabled = popoverItem.active === false;
          return (
            <div
              id={popoverItem.id}
              key={index}
              onClick={() => {
                if (disabled) return;
                popoverItem.action?.();
                setMenu(null);
              }}
              style={{
                padding: "12px 16px",
                borderTop: index === 0 ? "none" : "1px solid " + ColoursObj.shades.shade3,
                opacity: disabled ? 0.5 : 1,
              }}
            >
              <Typography variant="bodyRegular" color={popoverItem.color ?? ColoursObj.core.primary}>
                {popoverItem.title}
              </Typography>
            </div>
          );
        })}
      </div>
    </div>
  );
}
